import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { useSelector } from "react-redux";

import { useFetchMxfNestApi, useMxfNestApi } from "hooks";
import { useClientsAsync } from "useCases/suporte/useControlPainel";
import { getURLWithParams } from "utils/context";

const soDigitos = valor => String(valor ?? "").replace(/\D/g, "");

/* Converte o HTML de observação do grupo (grupo_obs) em texto limpo. */
const stripHtml = html => {
  if (!html) return "";
  const el = document.createElement("div");
  el.innerHTML = html;
  const zeroWidth = new RegExp(String.fromCharCode(0x200b), "g");
  return (el.textContent || el.innerText || "")
    .replace(zeroWidth, "")
    .replace(/\s+/g, " ")
    .trim();
};

/* Extrai portaria, datas e vigência do texto da observação do grupo. */
const parsePortaria = texto => {
  if (!texto) return {};
  const portaria = (/Portaria\s+SRE[-\s]?[\w/]+/i.exec(texto) || [])[0];
  const datas = texto.match(/\d{2}-\d{2}-\d{4}/g) || [];
  const vigencia = /em vigor em\s+(.+?)[.;]*$/i.exec(texto);
  return {
    portaria: portaria ? portaria.trim() : null,
    dataPublicacao: datas[0] || null,
    dataDoe: datas[1] || null,
    vigencia: vigencia ? vigencia[1].trim() : null,
  };
};

const fromRegra = regra =>
  regra
    ? {
        cst: regra.class_icms_cst,
        alq: regra.class_icms_alq,
        alqSt: regra.class_icms_alqst,
        rbc: regra.class_icms_rbc,
        rbcSt: regra.class_icms_rbcst,
      }
    : {};

/**
 * Lojas do usuário = lojas selecionadas no seletor de loja geral ("Visualizado").
 *
 * Pega os CNPJs das lojas selecionadas (state.app.header.storesSelecteds,
 * fallback header.stores) e resolve cada cliente (cli_id + nome) via
 * /mxf/clientes?cli_cnpj= (filtro exato). Retorna a lista [{ value, label, cnpj }].
 * A tela usa 1 loja por vez (APIs são por mxf_cli_id); quando houver mais de uma,
 * o usuário escolhe qual ver.
 */
export const useLojasSelecionadas = () => {
  const { fetchClients } = useClientsAsync();

  const cnpjsKey = useSelector(state => {
    const header = state?.app?.header;
    const stores =
      (header?.storesSelecteds?.length
        ? header.storesSelecteds
        : header?.stores) ?? [];
    return [
      ...new Set(stores.map(store => soDigitos(store?.cnpj)).filter(Boolean)),
    ].join(",");
  });

  const [lojas, setLojas] = useState([]);

  useEffect(() => {
    let ativo = true;
    const cnpjs = cnpjsKey ? cnpjsKey.split(",") : [];

    if (!cnpjs.length) {
      setLojas([]);
      return undefined;
    }

    (async () => {
      const resultados = await Promise.all(
        cnpjs.map(cnpj => fetchClients({ cli_cnpj: cnpj, limit: 1 })),
      );
      const mapa = new Map();
      resultados.forEach(resp => {
        const item = (resp?.data ?? [])[0];
        if (item?.cli_id && !mapa.has(item.cli_id)) {
          mapa.set(item.cli_id, {
            value: item.cli_id,
            label: item.cli_nome_fantasia || item.cli_nome,
            cnpj: item.cli_cnpj,
            uf: (item.cli_uf || "").toUpperCase().trim(),
          });
        }
      });
      if (ativo) setLojas([...mapa.values()]);
    })();

    return () => {
      ativo = false;
    };
  }, [cnpjsKey, fetchClients]);

  return lojas;
};

/**
 * 2º selector ("Caso Especial" / grupo de regras) — depende do cliente.
 * GET /produtos_impactados/listar_grupo_regras?mxf_cli_id={cli_id}
 * → { grupos: [{ grupo_id, grupo_nome }] } mapeado para { value, label }.
 */
export const useGrupoRegrasImpactados = cliId => {
  const { data, isError } = useFetchMxfNestApi(
    cliId
      ? getURLWithParams("/produtos_impactados/listar_grupo_regras", {
          mxf_cli_id: cliId,
        })
      : null,
    undefined,
    {
      revalidateIfStale: false,
      revalidateOnReconnect: false,
      dedupingInterval: 600000,
    },
  );

  // Ref estável — evita o Select reconstruir a lista ao trocar o caso.
  const grupos = useMemo(
    () =>
      (data?.grupos ?? []).map(grupo => ({
        value: grupo.grupo_id,
        label: grupo.grupo_nome,
      })),
    [data],
  );

  // Carregando só até a 1ª resposta (não re-dispara ao mudar o caso).
  return { grupos, isLoading: !!cliId && !data && !isError, isError };
};

/**
 * Casos impactados do cliente.
 * GET /produtos_impactados/listar_regras?mxf_cli_id={cli_id}
 * → { regras: [...] }. Cada regra vira um "caso" para a tela.
 *
 * Obs.: o endpoint hoje retorna todos os casos do cliente (não filtra por grupo).
 */
export const useRegrasImpactados = (cliId, grupoId) => {
  const { data, isLoading, isError } = useFetchMxfNestApi(
    cliId
      ? getURLWithParams("/produtos_impactados/listar_regras", {
          mxf_cli_id: cliId,
          ...(grupoId ? { id_grupo: grupoId } : {}),
        })
      : null,
  );

  const regras = data?.regras ?? [];

  const casos = regras
    .map((regra, index) => ({
      id: regra?.regras?.TANT?.regra_id ?? index,
      titulo: regra.caso_nome,
      grupoObsHtml: regra.grupo_obs,
      categoriaIds: regra.categorias?.lista_ids ?? [],
      setores: regra.categorias?.lista_setores ?? [],
      categorias: regra.categorias?.lista_categorias ?? [],
      qtdProdutos: regra.qtd_produtos ?? 0,
      cenarioAtual: fromRegra(regra?.regras?.TANT),
      cenarioNovo: fromRegra(regra?.regras?.TNOV),
    }))
    // Mais produtos primeiro (o primeiro caso abre por padrão na tela).
    .sort((a, b) => (b.qtdProdutos ?? 0) - (a.qtdProdutos ?? 0));

  const totalProdutos = casos.reduce(
    (acc, caso) => acc + (caso.qtdProdutos ?? 0),
    0,
  );

  const descricao = stripHtml(regras[0]?.grupo_obs);

  return {
    casos,
    descricao,
    portariaInfo: parsePortaria(descricao),
    totalProdutos,
    isLoading,
    isError,
  };
};

/**
 * Produtos afetados de um caso (paginado).
 * POST /produtos_impactados/listar_produtos
 * body: { mxf_cli_id, categorias: [ids], pagina, limite }
 * → { produtos: [...], total, pagina, limite }.
 */
export const useListarProdutos = ({
  cliId,
  categorias,
  codigos = [],
  eans = [],
  nomes = [],
  pagina = 1,
  limite = 20,
  enabled = true,
}) => {
  const api = useMxfNestApi();

  const active =
    enabled && !!cliId && Array.isArray(categorias) && categorias.length > 0;

  const key = active
    ? [
        "listar_produtos",
        cliId,
        categorias.join(","),
        codigos.join(","),
        eans.join(","),
        nomes.join("|"),
        pagina,
        limite,
      ]
    : null;

  const { data, error } = useSWR(key, async () => {
    const body = { mxf_cli_id: cliId, categorias, pagina, limite };
    if (codigos.length) body.codigos = codigos;
    if (eans.length) body.eans = eans;
    if (nomes.length) body.nomes = nomes;

    const resp = await api.post("/produtos_impactados/listar_produtos", body);
    return resp.data;
  });

  return {
    produtos: data?.produtos ?? [],
    total: data?.total ?? 0,
    isLoading: active && !data && !error,
    isError: !!error,
  };
};

/**
 * Download da lista de produtos impactados (CSV pronto do back-end).
 * GET /produtos_impactados/download_lista_produtos?mxf_cli_id={cli}&id_grupo={grupo}
 * → text/csv (com filename no Content-Disposition).
 */
export const useDownloadProdutosImpactados = () => {
  const api = useMxfNestApi();

  const downloadLista = async ({ cliId, grupoId }) => {
    const resp = await api.get(
      getURLWithParams("/produtos_impactados/download_lista_produtos", {
        mxf_cli_id: cliId,
        id_grupo: grupoId,
      }),
      { responseType: "blob", headers: { Accept: "text/csv" } },
    );

    const dispo = resp.headers?.["content-disposition"] ?? "";
    const match = /filename="?([^"]+)"?/.exec(dispo);
    const filename = match ? match[1] : `produtos-impactados-${cliId}.csv`;

    return { blob: resp.data, filename };
  };

  return { downloadLista };
};
